import { OfferCategory } from "../types/offers";
import {
  Restaurant,
  Checkroom,
  LocalMall,
  Devices,
  TheaterComedy,
  Face,
  MoreHoriz,
  Home,
  MenuBook,
  Diamond,
  FitnessCenter,
  SportsEsports,
  Toys,
  Computer,
  Flight,
} from "@mui/icons-material";

// UNIFIED COLOR SYSTEM: Same category = Same color across Offers & Events
// Category configuration with gradients and icons
export const categoryConfigs = {
  all: {
    key: "all" as const,
    label: "All Offers",
    icon: MoreHoriz,
    gradient: "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
    color: "#1a237e",
  },
  food: {
    key: "food" as const,
    label: "Food & Dining",
    icon: Restaurant,
    gradient: "linear-gradient(135deg, #e67e22 0%, #f39c12 100%)", // Orange (UNIFIED with Events)
    color: "#e67e22",
  },
  clothing: {
    key: "clothing" as const,
    label: "Clothing",
    icon: Checkroom,
    gradient: "linear-gradient(135deg, #ec008c 0%, #fc6767 100%)", // Hot pink
    color: "#ec008c",
  },
  footwear: {
    key: "footwear" as const,
    label: "Footwear",
    icon: LocalMall,
    gradient: "linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)", // Fresh green
    color: "#56ab2f",
  },
  electronics: {
    key: "electronics" as const,
    label: "Electronics",
    icon: Devices,
    gradient: "linear-gradient(135deg, #FC466B 0%, #3F5EFB 100%)", // Vibrant pink-blue
    color: "#FC466B",
  },
  entertainment: {
    key: "entertainment" as const,
    label: "Entertainment",
    icon: TheaterComedy,
    gradient: "linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)", // Purple (UNIFIED with Events)
    color: "#9b59b6",
  },
  beauty: {
    key: "beauty" as const,
    label: "Beauty & Makeup",
    icon: Face,
    gradient: "linear-gradient(135deg, #f857a6 0%, #ff5858 100%)", // Pink-coral (UNIFIED with Events)
    color: "#f857a6",
  },
  home: {
    key: "home" as const,
    label: "Home & Decor",
    icon: Home,
    gradient: "linear-gradient(135deg, #89216b 0%, #da4453 100%)", // Deep rose
    color: "#89216b",
  },
  books: {
    key: "books" as const,
    label: "Books & Stationery",
    icon: MenuBook,
    gradient: "linear-gradient(135deg, #f2994a 0%, #f2c94c 100%)", // Warm amber
    color: "#f2994a",
  },
  jewelry: {
    key: "jewelry" as const,
    label: "Jewelry & Accessories",
    icon: Diamond,
    gradient: "linear-gradient(135deg, #d4af37 0%, #ffd700 100%)", // Gold
    color: "#d4af37",
  },
  health: {
    key: "health" as const,
    label: "Health & Wellness",
    icon: FitnessCenter,
    gradient: "linear-gradient(135deg, #06beb6 0%, #48b1bf 100%)", // Aqua (UNIFIED with Events)
    color: "#06beb6",
  },
  sports: {
    key: "sports" as const,
    label: "Sports & Fitness",
    icon: SportsEsports,
    gradient: "linear-gradient(135deg, #16a085 0%, #1abc9c 100%)", // Teal (UNIFIED with Events)
    color: "#16a085",
  },
  toys: {
    key: "toys" as const,
    label: "Toys & Games",
    icon: Toys,
    gradient: "linear-gradient(135deg, #ff6b95 0%, #feca57 100%)", // Pink-yellow playful
    color: "#ff6b95",
  },
  tech: {
    key: "tech" as const,
    label: "Tech & Gadgets",
    icon: Computer,
    gradient: "linear-gradient(135deg, #34495e 0%, #2c3e50 100%)", // Steel gray (UNIFIED with Events)
    color: "#34495e",
  },
  travel: {
    key: "travel" as const,
    label: "Travel & Luggage",
    icon: Flight,
    gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)", // Sky blue
    color: "#4facfe",
  },
  others: {
    key: "others" as const,
    label: "Others",
    icon: MoreHoriz,
    gradient: "linear-gradient(135deg, #536976 0%, #292E49 100%)", // Slate gray
    color: "#536976",
  },
} as const;

// Get category config by key
export const getCategoryConfig = (category: OfferCategory | "all") => {
  return categoryConfigs[category] || categoryConfigs.others;
};

// Get category gradient for visual styling
export const getCategoryGradient = (category: OfferCategory): string => {
  const config = categoryConfigs[category as keyof typeof categoryConfigs];
  return config?.gradient || categoryConfigs.others.gradient;
};

// Format discount text
export const formatDiscount = (
  discountType: string,
  discountValue?: number
): string => {
  switch (discountType) {
    case "percentage":
      return `${discountValue}% OFF`;
    case "flat":
      return `₹${discountValue} OFF`;
    case "bogo":
      return "BUY 1 GET 1";
    case "freebie":
      return "FREE ITEM";
    case "combo":
      return "COMBO DEAL";
    default:
      return "SPECIAL OFFER";
  }
};

// Format date for display
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };
  return date.toLocaleDateString("en-IN", options);
};

// Calculate days remaining
export const getDaysRemaining = (validUntil: string): number => {
  const today = new Date();
  const endDate = new Date(validUntil);
  const diffTime = endDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Check if offer is expiring soon (within 3 days)
export const isExpiringSoon = (validUntil: string): boolean => {
  const daysRemaining = getDaysRemaining(validUntil);
  return daysRemaining <= 3 && daysRemaining > 0;
};

// Check if offer has expired
export const isExpired = (validUntil: string): boolean => {
  return getDaysRemaining(validUntil) < 0;
};

// Get validity status text
export const getValidityStatus = (validUntil: string): string => {
  const days = getDaysRemaining(validUntil);
  if (days < 0) return "Expired";
  if (days === 0) return "Expires today";
  if (days === 1) return "Expires tomorrow";
  if (days <= 3) return `${days} days left`;
  return `Valid until ${formatDate(validUntil)}`;
};

// Share offer via Web Share API or fallback
export const shareOffer = async (offer: {
  title: string;
  description: string;
  url?: string;
}): Promise<boolean> => {
  console.log("Share function called");
  console.log("Navigator.share available:", !!navigator.share);
  console.log("Navigator.clipboard available:", !!navigator.clipboard);

  const shareUrl = offer.url || window.location.href;

  // Check if Web Share API is available
  // @ts-ignore - navigator.share may not be available in all browsers
  if ("share" in navigator) {
    try {
      console.log("Attempting to share with Web Share API...");
      await navigator.share({
        title: offer.title,
        text: offer.description,
        url: shareUrl,
      });
      console.log("Share successful");
      return true;
    } catch (error: any) {
      console.log("Share error:", error.name, error.message);
      // User cancelled the share dialog
      if (error.name === "AbortError") {
        console.log("Share cancelled by user");
        return false;
      }
      // Other errors - fallback to clipboard
      console.error("Error sharing:", error);
      return await copyToClipboard(offer);
    }
  } else {
    // Fallback: Copy to clipboard or direct share links
    console.log("Web Share API not available, using clipboard fallback");
    return await copyToClipboard(offer);
  }
};

// Helper function to copy offer details to clipboard
const copyToClipboard = async (offer: {
  title: string;
  description: string;
  url?: string;
}): Promise<boolean> => {
  const text = `${offer.title}\n\n${offer.description}\n\nCheck it out: ${
    offer.url || window.location.href
  }`;

  // Check if clipboard API is available
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      console.log("Attempting to copy to clipboard...");
      await navigator.clipboard.writeText(text);
      console.log("Copied to clipboard successfully");
      return true;
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      return false;
    }
  } else {
    console.log("Clipboard API not available - requires HTTPS");
    return false;
  }
};

// Generate WhatsApp share link
export const getWhatsAppShareLink = (text: string, url?: string): string => {
  const shareText = url ? `${text}\n\n${url}` : text;
  return `https://wa.me/?text=${encodeURIComponent(shareText)}`;
};

// Generate email share link
export const getEmailShareLink = (subject: string, body: string): string => {
  return `mailto:?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;
};

// Generate SMS share link
export const getSMSShareLink = (text: string): string => {
  return `sms:?body=${encodeURIComponent(text)}`;
};
